import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { storyApi, type RunStory } from "./api";
import { DECISION, ROLE } from "./ResultCard";
import { UI, useLang } from "./lang";

/**
 * "How this was made" (NEW-UX-7), for a person rather than an auditor: the
 * run in five plain steps, from its rows. The full audit — rules in force,
 * the model call, every validator — stays one link away on the console.
 */

type B = { en: string; ar: string };
const S = {
  worked: { en: "What it worked out", ar: "ما حسبه" },
  workedNote: { en: "Computed by code from your data — not by the model.", ar: "حسبته الشيفرة من بياناتك — وليس النموذج." },
  checked: { en: "What it checked against", ar: "ما تحقق منه" },
  checkedLive: { en: "Rules from the regulatory memory, pack", ar: "القواعد من الذاكرة التنظيمية، الإصدار" },
  checkedOffline: { en: "Rules from the offline snapshot, pack", ar: "القواعد من النسخة غير المتصلة، الإصدار" },
  noChecks: { en: "No rule applied to this run.", ar: "لم تنطبق أي قاعدة على هذا التشغيل." },
  wrote: { en: "What it wrote", ar: "ما كتبه" },
  wroteNote: { en: "Every figure was put in by code.", ar: "كل رقم أدخلته الشيفرة." },
  withheld: { en: "The draft is not shown because it failed its checks:", ar: "لا تُعرض المسودة لأنها لم تجتز فحوصها:" },
  marked: { en: "This contains personal data, so the draft is only in the full record.", ar: "يحتوي هذا على بيانات شخصية، فالمسودة في السجل الكامل فقط." },
  noDraft: { en: "It stopped before writing anything.", ar: "توقف قبل أن يكتب أي شيء." },
  decides: { en: "Who decides", ar: "من يقرر" },
  done: { en: "Done — nothing waits on anyone.", ar: "تم — لا شيء بانتظار أحد." },
  audit: { en: "Full audit record", ar: "السجل الكامل للتدقيق" },
  placeholder: { en: "Placeholder", ar: "قيمة مؤقتة" },
  answerTitle: { en: "How this answer was made", ar: "كيف صيغت هذه الإجابة" },
  lookedUp: { en: "What it looked up", ar: "ما بحث عنه" },
  noLookups: { en: "It looked nothing up; the answer holds no figures.", ar: "لم يبحث عن شيء؛ لا تحتوي الإجابة على أرقام." },
  found: { en: "Found", ar: "وُجد" },
  noData: { en: "No data", ar: "لا بيانات" },
  answerNote: { en: "Every figure in the answer was put in by code from these lookups; the model wrote only the sentences.", ar: "كل رقم في الإجابة أدخلته الشيفرة من عمليات البحث هذه؛ النموذج كتب الجمل فقط." },
  took: { en: "Took", ar: "استغرق" },
  seconds: { en: "s", ar: " ث" },
} satisfies Record<string, B>;

const OUTCOME: Record<RunStory["checks"][number]["outcome"], B & { cls: string }> = {
  passed: { en: "Passed", ar: "اجتاز", cls: "ok" },
  stopped: { en: "Stopped it", ar: "أوقفه", cls: "stop" },
  flagged: { en: "Flagged", ar: "نبّه", cls: "warn" },
  needs_person: { en: "A person must confirm", ar: "يلزم تأكيد شخص", cls: "ask" },
};

type Outcome = RunStory["checks"][number]["outcome"];
const RANK: Record<Outcome, number> = { stopped: 0, flagged: 1, needs_person: 2, passed: 3 };
/** Rules shown before "Show all". */
const FIRST = 5;

/** A rule's sentence without the pack's placeholder disclaimer, which becomes a tag. */
function plain(summary: string): { text: string; placeholder: boolean } {
  const m = /\s*\(placeholder value[^)]*\)\s*/i;
  return { text: summary.replace(m, " ").trim(), placeholder: m.test(summary) || /^placeholder floor/i.test(summary) };
}

function countLine(checks: RunStory["checks"], t: (b: B) => string): string {
  const n = (o: Outcome) => checks.filter((c) => c.outcome === o).length;
  const parts: string[] = [t({ en: `${checks.length} rules`, ar: `${checks.length} قاعدة` })];
  if (n("stopped")) parts.push(t({ en: `${n("stopped")} stopped it`, ar: `${n("stopped")} أوقفته` }));
  if (n("flagged")) parts.push(t({ en: `${n("flagged")} flagged`, ar: `${n("flagged")} نبّهت` }));
  if (n("passed")) parts.push(t({ en: `${n("passed")} passed`, ar: `${n("passed")} اجتازت` }));
  if (n("needs_person")) parts.push(t({ en: `${n("needs_person")} need a person`, ar: `${n("needs_person")} تحتاج شخصاً` }));
  return parts.join(" · ");
}

export function RunInApp() {
  const { workspaceId = "", runId = "" } = useParams<{ workspaceId: string; runId: string }>();
  const { t } = useLang();
  const [showAll, setShowAll] = useState(false);
  const q = useQuery<RunStory, Error>({
    queryKey: ["run-story", workspaceId, runId],
    enabled: !!workspaceId && !!runId,
    queryFn: () => storyApi.get(workspaceId, runId),
  });

  const back = (
    <Link className="pwa-back" to={`/app/${workspaceId}/thread`}>
      {t(UI.back)}
    </Link>
  );
  if (!q.data) {
    return (
      <div className="pwa-run">
        {back}
        <div className="pwa-empty">{q.isError ? "That run could not be found." : "…"}</div>
      </div>
    );
  }

  const s = q.data;
  const v = s.view;
  // What needs attention first: stopped, then flagged, then unanswered, then passed.
  const ranked = [...s.checks].sort((a, b) => RANK[a.outcome] - RANK[b.outcome]);
  const fork = v.fork;
  const inline = v.marking === "public" || v.marking === "internal";
  const draft = t({ en: s.draft_en ?? "", ar: s.draft_ar ?? s.draft_en ?? "" });
  const failed = t({ en: s.checks_failed_en.join("\n"), ar: s.checks_failed_ar.join("\n") }).split("\n").filter(Boolean);

  if (s.answer) {
    // An answer: what it looked up, in words. Every figure in it was put in
    // by code from these lookups; the model wrote only the sentences.
    return (
      <div className="pwa-run">
        {back}
        <div className="pwa-list story">
          <header className="story-head">
            <h1 className="story-title">{t(S.answerTitle)}</h1>
            <div className="story-meta">
              {s.took_seconds > 0 ? (
                <span>
                  {t(S.took)} {s.took_seconds}
                  {t(S.seconds)}
                </span>
              ) : null}
              {s.model ? <span>{s.model}</span> : null}
            </div>
          </header>
          <section className="story-step">
            <h2>{t(S.lookedUp)}</h2>
            {s.answer.lookups.length === 0 ? (
              <p className="story-note">{t(S.noLookups)}</p>
            ) : (
              <ul className="story-checks">
                {s.answer.lookups.map((l, i) => (
                  <li key={i} data-testid={`lookup-${i}`}>
                    <span className={`story-outcome ${l.found ? "ok" : "ask"}`}>{t(l.found ? S.found : S.noData)}</span>
                    <span className="story-rule">
                      {t(l.label)}
                      {l.detail ? <span className="story-source">{l.detail}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="story-note">{t(S.answerNote)}</p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="pwa-run">
      {back}
      <div className="pwa-list story">
        <header className="story-head">
          <h1 className="story-title">{t({ en: v.hand_name, ar: v.hand_name_ar ?? v.hand_name })}</h1>
          <div className="story-meta">
            {fork?.subject ? <span>{fork.subject.value}</span> : null}
            {s.took_seconds > 0 ? (
              <span>
                {t(S.took)} {s.took_seconds}
                {t(S.seconds)}
              </span>
            ) : null}
            {s.model ? <span>{s.model}</span> : null}
          </div>
        </header>

        {v.tiles.length > 0 ? (
          <section className="story-step">
            <h2>{t(S.worked)}</h2>
            <div className="pwa-tiles">
              {v.tiles.map((x) => (
                <div key={x.label_en} className="pwa-tile">
                  <div className="lbl">{t({ en: x.label_en, ar: x.label_ar })}</div>
                  <div className="val">{x.value}</div>
                </div>
              ))}
            </div>
            <p className="story-note">{t(S.workedNote)}</p>
          </section>
        ) : null}

        <section className="story-step">
          <h2>{t(S.checked)}</h2>
          {s.checks.length === 0 ? (
            <p className="story-note">{t(S.noChecks)}</p>
          ) : (
            <>
              <p className="story-count" data-testid="story-check-count">
                {countLine(s.checks, t)}
              </p>
              <ul className="story-checks">
                {(showAll ? ranked : ranked.slice(0, FIRST)).map((c) => {
                  const o = OUTCOME[c.outcome];
                  const { text, placeholder } = plain(t({ en: c.summary, ar: c.summary_ar ?? c.summary }));
                  return (
                    <li key={c.rule_id} data-testid={`check-${c.rule_id}`}>
                      <span className={`story-outcome ${o.cls}`}>{t(o)}</span>
                      <span className="story-rule">
                        {text}
                        <span className="story-source">
                          {placeholder ? <span className="story-tag">{t(S.placeholder)}</span> : null}
                          {c.source}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              {!showAll && ranked.length > FIRST ? (
                <button type="button" className="story-more" data-testid="story-show-all" onClick={() => setShowAll(true)}>
                  {t({ en: `Show all ${ranked.length}`, ar: `عرض الكل (${ranked.length})` })}
                </button>
              ) : null}
            </>
          )}
          {s.pack_version ? (
            <p className="story-note">
              {t(s.rules_live ? S.checkedLive : S.checkedOffline)} {s.pack_version}
            </p>
          ) : null}
        </section>

        <section className="story-step">
          <h2>{t(S.wrote)}</h2>
          {failed.length > 0 ? (
            <div className="story-withheld" data-testid="story-withheld">
              <p>{t(S.withheld)}</p>
              <ul>
                {failed.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          ) : !inline ? (
            <p className="story-note">{t(S.marked)}</p>
          ) : s.draft_en ? (
            <>
              <div className="story-draft" data-testid="story-draft">
                {draft}
              </div>
              {s.figures_substituted > 0 ? <p className="story-note">{t(S.wroteNote)}</p> : null}
            </>
          ) : (
            <p className="story-note">{t(S.noDraft)}</p>
          )}
        </section>

        <section className="story-step">
          <h2>{t(S.decides)}</h2>
          <p data-testid="story-decides">
            {fork?.resolution
              ? `${t(DECISION[fork.resolution.decision] ?? { en: fork.resolution.decision, ar: fork.resolution.decision })} — ${t(ROLE[fork.resolution.resolved_role] ?? { en: fork.resolution.resolved_role, ar: fork.resolution.resolved_role })}: “${fork.resolution.reason}”`
              : fork
                ? t({ en: `Waiting on ${ROLE[fork.approver_role]?.en ?? fork.approver_role}.`, ar: `بانتظار ${ROLE[fork.approver_role]?.ar ?? fork.approver_role}.` })
                : t(S.done)}
          </p>
        </section>

        <Link className="story-audit" to={`/workspaces/${workspaceId}/workforce/runs/${v.run_id}`}>
          {t(S.audit)} →
        </Link>
      </div>
    </div>
  );
}
